/**
 * The ten curated report palettes — Document 6 §3.
 *
 * Components reference tokens only, never a literal colour. A palette is
 * applied by setting these eleven CSS custom properties on the report root;
 * that is the only mechanism (Doc 6 §3.3).
 *
 * Adding an eleventh palette must not require touching any component.
 */

export const PALETTE_KEYS = [
  'palette_01',
  'palette_02',
  'palette_03',
  'palette_04',
  'palette_05',
  'palette_06',
  'palette_07',
  'palette_08',
  'palette_09',
  'palette_10',
] as const;

export type PaletteKey = (typeof PALETTE_KEYS)[number];

export const DEFAULT_PALETTE_KEY: PaletteKey = 'palette_01';

export interface PaletteTokens {
  bg: string;
  surface: string;
  surfaceAlt: string;
  ink: string;
  inkMuted: string;
  primary: string;
  primaryStrong: string;
  accent: string;
  border: string;
  chart: string;
  chartGrid: string;
}

export interface Palette {
  key: PaletteKey;
  /** Shown in the studio settings picker. */
  name: string;
  tokens: PaletteTokens;
}

export const PALETTES: Record<PaletteKey, Palette> = {
  palette_01: {
    key: 'palette_01',
    name: 'Adaçayı',
    tokens: {
      bg: '#FBF9F4',
      surface: '#F2EFE6',
      surfaceAlt: '#EAE5D8',
      ink: '#2E3A31',
      inkMuted: '#5C6A5B',
      primary: '#40573F',
      primaryStrong: '#2F4230',
      accent: '#C2A25E',
      border: '#DDD6C6',
      chart: '#526B4F',
      chartGrid: '#E3DDCE',
    },
  },
  palette_02: {
    key: 'palette_02',
    name: 'Kil',
    tokens: {
      bg: '#FCF8F4',
      surface: '#F4EDE6',
      surfaceAlt: '#ECE1D6',
      ink: '#3A2E28',
      inkMuted: '#736055',
      primary: '#8E4830',
      primaryStrong: '#6E3623',
      accent: '#C08A52',
      border: '#E2D5C7',
      chart: '#A8583A',
      chartGrid: '#EDE2D6',
    },
  },
  palette_03: {
    key: 'palette_03',
    name: 'Arduvaz',
    tokens: {
      bg: '#F8F9FA',
      surface: '#EEF1F3',
      surfaceAlt: '#E3E8EC',
      ink: '#232B31',
      inkMuted: '#5F6B74',
      primary: '#34495A',
      primaryStrong: '#22323F',
      accent: '#8A9AA8',
      border: '#D5DCE2',
      chart: '#3F5A70',
      chartGrid: '#E2E8ED',
    },
  },
  palette_04: {
    key: 'palette_04',
    name: 'Gül Kurusu',
    tokens: {
      bg: '#FDF8F7',
      surface: '#F6ECEA',
      surfaceAlt: '#EFDFDC',
      ink: '#35292A',
      inkMuted: '#745E61',
      primary: '#8C4A55',
      primaryStrong: '#6B343E',
      accent: '#C08C7A',
      border: '#E7D5D2',
      chart: '#9C5561',
      chartGrid: '#EFE0DE',
    },
  },
  palette_05: {
    key: 'palette_05',
    name: 'Kömür',
    tokens: {
      bg: '#FAFAF8',
      surface: '#F0F0ED',
      surfaceAlt: '#E5E5E1',
      ink: '#1C1C1A',
      inkMuted: '#61615C',
      primary: '#2B2B28',
      primaryStrong: '#161614',
      accent: '#A8894F',
      border: '#DCDCD6',
      chart: '#3A3A36',
      chartGrid: '#E4E4DF',
    },
  },
  palette_06: {
    key: 'palette_06',
    name: 'Okyanus',
    tokens: {
      bg: '#F7FAFA',
      surface: '#E9F1F0',
      surfaceAlt: '#DBE8E6',
      ink: '#1E2E2E',
      inkMuted: '#5A7170',
      primary: '#245B58',
      primaryStrong: '#17403E',
      accent: '#B99A5E',
      border: '#CFE0DD',
      chart: '#2E6E6A',
      chartGrid: '#DFEBE9',
    },
  },
  palette_07: {
    key: 'palette_07',
    name: 'Lavanta',
    tokens: {
      bg: '#FAF8FC',
      surface: '#F0ECF5',
      surfaceAlt: '#E5DEEE',
      ink: '#2B2733',
      inkMuted: '#6A6279',
      primary: '#5A4A78',
      primaryStrong: '#40355A',
      accent: '#B49A62',
      border: '#DCD4E6',
      chart: '#6B5890',
      chartGrid: '#E6DFEF',
    },
  },
  palette_08: {
    key: 'palette_08',
    name: 'Kum',
    tokens: {
      bg: '#FBF8F3',
      surface: '#F2ECE1',
      surfaceAlt: '#E8DFCF',
      ink: '#322B22',
      inkMuted: '#6F6453',
      primary: '#6B5842',
      primaryStrong: '#4C3E2D',
      accent: '#BFA06A',
      border: '#E0D5C1',
      chart: '#7D6849',
      chartGrid: '#EAE1D0',
    },
  },
  palette_09: {
    key: 'palette_09',
    name: 'Erik',
    tokens: {
      bg: '#FCF8F9',
      surface: '#F4EBEE',
      surfaceAlt: '#EBDCE1',
      ink: '#2F2429',
      inkMuted: '#6F5C63',
      primary: '#6E2C43',
      primaryStrong: '#4F1E30',
      accent: '#B08A63',
      border: '#E4D3D8',
      chart: '#83364F',
      chartGrid: '#EEE0E4',
    },
  },
  palette_10: {
    key: 'palette_10',
    name: 'Gece',
    tokens: {
      bg: '#F8F9FB',
      surface: '#ECEFF4',
      surfaceAlt: '#DFE4EC',
      ink: '#1D2430',
      inkMuted: '#5B6675',
      primary: '#22344F',
      primaryStrong: '#14223A',
      accent: '#B0925C',
      border: '#D3DAE4',
      chart: '#2E4666',
      chartGrid: '#E1E6EE',
    },
  },
};

export function isPaletteKey(value: unknown): value is PaletteKey {
  return typeof value === 'string' && (PALETTE_KEYS as readonly string[]).includes(value);
}

export function getPalette(key: string): Palette {
  return isPaletteKey(key) ? PALETTES[key] : PALETTES[DEFAULT_PALETTE_KEY];
}

/** Maps a palette to the CSS custom properties the report root expects. */
export function paletteToCssVars(palette: Palette): Record<string, string> {
  const t = palette.tokens;
  return {
    '--pp-bg': t.bg,
    '--pp-surface': t.surface,
    '--pp-surface-alt': t.surfaceAlt,
    '--pp-ink': t.ink,
    '--pp-ink-muted': t.inkMuted,
    '--pp-primary': t.primary,
    '--pp-primary-strong': t.primaryStrong,
    '--pp-accent': t.accent,
    '--pp-border': t.border,
    '--pp-chart': t.chart,
    '--pp-chart-grid': t.chartGrid,
  };
}

export const TEMPLATE_KEYS = ['template_01'] as const;
export type TemplateKey = (typeof TEMPLATE_KEYS)[number];
export const DEFAULT_TEMPLATE_KEY: TemplateKey = 'template_01';
